<template>
    <div class="fuel-cards-page">
        <b-row class="my-2 align-items-center">
            <b-col cols="12" md="5" class="mb-2 mb-md-0">
                <b-input-group size="sm">
                    <b-form-input
                            v-model="filter"
                            type="search"
                            placeholder="Введите для поиска"
                    ></b-form-input>
                </b-input-group>
            </b-col>
            <b-col cols="12" md="3" class="mb-2 mb-md-0">
                <div class="d-flex">
                    <b-form-select
                            v-model="statusFilter"
                            :options="statusFilterOptions"
                            size="sm"
                            class="mr-2"
                            aria-label="Статус карты"
                    ></b-form-select>
                    <b-form-select
                            v-model="archiveOnly"
                            :options="archiveScopeOptions"
                            size="sm"
                            aria-label="Архив топливных карт"
                    ></b-form-select>
                </div>
            </b-col>
            <b-col cols="12" md="4">
                <div class="d-flex flex-wrap justify-content-md-end align-items-center">
                    <b-pagination
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            align="fill"
                            size="sm"
                            class="my-0 mb-2 mb-md-0 mr-2"
                    ></b-pagination>
                    <b-form-group
                            label="На странице"
                            label-cols="6"
                            label-size="sm"
                            label-for="fuelCardsPerPage"
                            class="mb-2 mb-md-0 d-flex align-items-center"
                    >
                        <b-form-select
                                v-model="perPage"
                                id="fuelCardsPerPage"
                                size="sm"
                                :options="pageOptions"
                        ></b-form-select>
                    </b-form-group>
                </div>
            </b-col>
        </b-row>

        <div class="d-flex justify-content-end my-2">
            <b-button
                    v-if="canManageAccess"
                    size="sm"
                    variant="outline-primary"
                    class="mr-2"
                    @click="openAccessModal"
            >
                <i class="fas fa-user-shield mr-1"></i>
                Настроить доступ
            </b-button>
            <b-button
                    v-if="canCreate"
                    size="sm"
                    variant="success"
                    @click="openCreateModal"
            >
                <i class="fas fa-plus mr-1"></i>
                Добавить карту
            </b-button>
        </div>

        <b-alert
                v-if="errorMessage"
                variant="danger"
                show
                dismissible
                @dismissed="errorMessage = null"
                class="mb-2"
        >
            {{ errorMessage }}
        </b-alert>

        <b-table
                responsive="sm"
                :fields="computedFields"
                :items="visibleItems"
                :current-page="currentPage"
                :per-page="perPage"
                striped
                small
                :busy="isCardsLoading"
                sticky-header="80vh"
        >
            <template #cell(status)="data">
                <b-badge :variant="statusVariant(data.item.status)">
                    {{ statusLabel(data.item.status) }}
                </b-badge>
            </template>
            <template #cell(allowed_fuel_type_names)="data">
                <span v-if="data.item.allowed_fuel_type_names && data.item.allowed_fuel_type_names.length">
                    {{ data.item.allowed_fuel_type_names.join(', ') }}
                </span>
                <span v-else class="text-muted">Все</span>
            </template>
            <template #cell(fuel_limits)="data">
                <span v-if="formatFuelLimits(data.item.fuel_limits)">
                    {{ formatFuelLimits(data.item.fuel_limits) }}
                </span>
                <span v-else class="text-muted">Не указаны</span>
            </template>
            <template #cell(expires_at)="data">
                <span>{{ formatDate(data.item.expires_at) }}</span>
            </template>
            <template #cell(actions)="data">
                <b-button
                        v-if="canReadHistory(data.item)"
                        size="sm"
                        variant="outline-secondary"
                        class="mr-1 mb-1"
                        title="История изменений"
                        @click="openHistoryModal(data.item)"
                >
                    <i class="fas fa-history"></i>
                </b-button>
                <b-button
                        v-if="canEditCard(data.item)"
                        size="sm"
                        variant="outline-success"
                        class="mr-1 mb-1"
                        title="Редактировать"
                        @click="openEditModal(data.item)"
                >
                    <i class="fas fa-pen"></i>
                </b-button>
                <b-button
                        v-if="canArchiveCard(data.item) && data.item.status !== 'archive'"
                        size="sm"
                        variant="outline-danger"
                        class="mb-1"
                        title="Архивировать"
                        @click="openArchiveModal(data.item)"
                >
                    <i class="fas fa-archive"></i>
                </b-button>
            </template>
        </b-table>

        <b-modal
                v-model="showEditModal"
                :title="isEditMode ? 'Редактирование топливной карты' : 'Создание топливной карты'"
                ok-title="Сохранить"
                cancel-title="Отмена"
                :ok-disabled="!isFormValid || isSubmitting"
                :busy="isSubmitting"
                @ok="submitForm"
                @hidden="resetForm"
        >
            <b-form @submit.stop.prevent="submitForm">
                <b-alert
                        v-if="modalErrorMessage"
                        variant="danger"
                        show
                        class="mb-2"
                >
                    {{ modalErrorMessage }}
                </b-alert>
                <b-alert
                        v-if="formValidationMessages.length"
                        variant="warning"
                        show
                        class="mb-2"
                >
                    <div
                            v-for="message in formValidationMessages"
                            :key="message"
                    >
                        {{ message }}
                    </div>
                </b-alert>

                <b-form-group label="Номер карты" label-for="fuelCardNumber">
                    <b-form-input
                            id="fuelCardNumber"
                            v-model.trim="form.card_number"
                            :state="cardNumberState"
                            required
                    ></b-form-input>
                    <b-form-invalid-feedback>
                        Укажите номер топливной карты.
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Статус" label-for="fuelCardStatus">
                    <b-form-select
                            id="fuelCardStatus"
                            v-model="form.status"
                            :options="statusOptions"
                            required
                    ></b-form-select>
                </b-form-group>

                <b-form-group label="Строительный процесс" label-for="fuelCardProcess">
                    <v-select
                            id="fuelCardProcess"
                            :options="objectOptions"
                            label="displayName"
                            :clearable="true"
                            :value="selectedProcessOption"
                            placeholder="Выберите объект"
                            @input="onSelectProcess"
                    />
                    <small class="text-muted">
                        Можно оставить пустым, если карта пока не привязана к объекту.
                    </small>
                </b-form-group>

                <b-form-group label="Ответственный сотрудник" label-for="fuelCardResponsible">
                    <v-select
                            id="fuelCardResponsible"
                            :class="{'fuel-cards-select-invalid': responsibleState === false}"
                            :options="userOptions"
                            label="displayName"
                            :clearable="true"
                            :value="selectedResponsibleOption"
                            placeholder="Выберите сотрудника"
                            @input="onSelectResponsible"
                    />
                    <div v-if="responsibleState === false" class="invalid-feedback d-block">
                        Укажите ответственного сотрудника.
                    </div>
                </b-form-group>

                <b-form-group label="Поставщик / сеть" label-for="fuelCardProvider">
                    <b-form-input
                            id="fuelCardProvider"
                            v-model.trim="form.provider"
                            placeholder="Например, Белоруснефть"
                    ></b-form-input>
                </b-form-group>

                <b-form-group label="Разрешенные виды топлива">
                    <b-form-checkbox-group
                            v-model="form.allowed_fuel_type_ids"
                            :options="fuelTypeOptions"
                            stacked
                    ></b-form-checkbox-group>
                    <small class="text-muted">Если ничего не выбрано, карта считается без ограничения по виду топлива.</small>
                </b-form-group>

                <b-form-group label="Суточные лимиты топлива по карте">
                    <div
                            v-for="(limit, index) in form.fuel_limits"
                            :key="`fuel-limit-${index}`"
                            class="d-flex align-items-center mb-2"
                    >
                        <b-form-select
                                v-model.number="limit.fuel_type_id"
                                :options="fuelLimitTypeOptions"
                                size="sm"
                                class="mr-2"
                                :data-fuel-card-limit-form-id="_uid"
                                data-fuel-card-limit-type
                                :data-fuel-card-limit-index="index"
                                @change="setFuelLimitType(limit, $event)"
                        ></b-form-select>
                        <b-form-input
                                v-model.number="limit.value"
                                type="number"
                                min="0"
                                step="0.001"
                                size="sm"
                                placeholder="Лимит в сутки"
                                class="mr-2"
                                :data-fuel-card-limit-form-id="_uid"
                                data-fuel-card-limit-value
                                :data-fuel-card-limit-index="index"
                                @input="setFuelLimitValue(limit, $event)"
                                @change="setFuelLimitValue(limit, $event)"
                        ></b-form-input>
                        <b-button
                                size="sm"
                                variant="outline-danger"
                                title="Удалить лимит"
                                @click="removeFuelLimit(index)"
                        >
                            <i class="fas fa-times"></i>
                        </b-button>
                    </div>
                    <b-button size="sm" variant="outline-primary" @click="addFuelLimit">
                        <i class="fas fa-plus mr-1"></i>
                        Добавить лимит
                    </b-button>
                    <small class="text-muted d-block mt-1">
                        Значение указывается в сутки, в единицах выбранного вида топлива.
                    </small>
                </b-form-group>

                <b-form-group label="Срок действия" label-for="fuelCardExpiresAt">
                    <b-form-input
                            id="fuelCardExpiresAt"
                            v-model="form.expires_at"
                            type="date"
                    ></b-form-input>
                </b-form-group>

                <b-form-group label="Комментарий" label-for="fuelCardComment">
                    <AppTextarea
                            id="fuelCardComment"
                            v-model.trim="form.comment"
                    ></AppTextarea>
                </b-form-group>
            </b-form>
        </b-modal>

        <b-modal
                v-model="showArchiveModal"
                title="Архивация топливной карты"
                ok-title="Архивировать"
                ok-variant="danger"
                cancel-title="Отмена"
                :busy="isSubmitting"
                @ok.prevent="archiveCurrentCard"
                @hide="currentCard = null"
        >
            <p class="mb-0">
                Архивировать топливную карту
                <b>{{ currentCard ? currentCard.card_number : '' }}</b>?
            </p>
        </b-modal>

        <b-modal
                v-model="showHistoryModal"
                size="lg"
                title="История топливной карты"
                ok-only
                ok-title="Закрыть"
                @hide="resetHistory"
        >
            <b-table
                    :items="historyRows"
                    :fields="historyFields"
                    small
                    responsive="sm"
                    :busy="isHistoryLoading"
                    show-empty
                    empty-text="История пуста"
            >
                <template #cell(created_at)="data">
                    {{ formatDateTime(data.item.created_at) }}
                </template>
                <template #cell(action)="data">
                    {{ historyActionLabel(data.item.action) }}
                </template>
                <template #cell(request)="data">
                    <router-link
                            v-if="data.item.request_id"
                            :to="`/requests/${data.item.request_id}`"
                    >
                        #{{ data.item.request_number || data.item.request_id }}
                    </router-link>
                    <span v-else class="text-muted">-</span>
                </template>
                <template #cell(changes)="data">
                    <small>{{ formatHistoryChanges(data.item.changes) }}</small>
                </template>
                <template #cell(comment)="data">
                    <small>{{ data.item.comment || '-' }}</small>
                </template>
            </b-table>
        </b-modal>

        <b-modal
                v-model="showAccessModal"
                size="lg"
                title="Настройка доступа к топливным картам"
                ok-title="Сохранить"
                cancel-title="Отмена"
                :busy="isSettingsSubmitting"
                :ok-disabled="isSettingsSubmitting"
                @ok.prevent="saveAccessSettings"
                @hide="resetAccessModal"
        >
            <b-alert show variant="info" class="mb-3">
                Роли на объекте дают доступ только к картам, у которых указан строительный процесс этого объекта.
                Карты без объекта по таким правилам не видны.
            </b-alert>

            <b-form-checkbox v-model="settingsForm.enabled" switch class="mb-3">
                Использовать настройку доступа
            </b-form-checkbox>

            <div v-if="settingsForm.enabled">
                <b-form-checkbox v-model="settingsForm.read_history_for_readers" switch class="mb-3">
                    Разрешить историю пользователям только с правом чтения
                    <small class="text-muted d-block">
                        Если выключено, историю изменений видят только админы и пользователи с правом редактирования.
                        Это скрывает прежние объекты карты от пользователей, которым доступен только текущий объект.
                    </small>
                </b-form-checkbox>

                <div class="border rounded p-3 mb-3">
                    <h6 class="mb-2">
                        <i class="fas fa-eye"></i>
                        Кто видит справочник
                    </h6>
                    <MultipleParticipantsConfig
                            v-model="settingsForm.read"
                            :users="accessUserOptions"
                            :departments="accessDepartmentOptions"
                            participant-label="правило чтения"
                            :show-object-roles="true"
                            :show-fallback-option="false"
                            type-label="Тип правила"
                            all-text="Правило распространяется на всех пользователей системы."
                            hint-text="Правила работают по ИЛИ: достаточно совпадения с одним правилом."
                            empty-text="Правила чтения не добавлены."
                    />
                </div>

                <div class="border rounded p-3">
                    <h6 class="mb-2">
                        <i class="fas fa-pen"></i>
                        Кто редактирует справочник
                    </h6>
                    <MultipleParticipantsConfig
                            v-model="settingsForm.write"
                            :users="accessUserOptions"
                            :departments="accessDepartmentOptions"
                            participant-label="правило редактирования"
                            :show-object-roles="true"
                            :show-fallback-option="false"
                            type-label="Тип правила"
                            all-text="Правило распространяется на всех пользователей системы."
                            hint-text="Право редактирования также дает чтение доступных карт."
                            empty-text="Правила редактирования не добавлены."
                    />
                </div>
            </div>

            <b-alert v-else show variant="secondary" class="mb-0">
                Пока настройка выключена, работают прежние права системы.
            </b-alert>
        </b-modal>
    </div>
</template>

<script>
    import MultipleParticipantsConfig from "@/views/Requests/RequestTypes/MultipleParticipantsConfig/MultipleParticipantsConfig.vue";
    import {
        buildFuelTypeById,
        buildFuelTypeOptions,
        cloneFuelLimits as cloneFuelLimitsValue,
        formatFuelLimits as formatFuelLimitsValue,
        sanitizeFuelLimits as sanitizeFuelLimitsValue,
        setFuelLimitType as setFuelLimitTypeValue,
        setFuelLimitValue as setFuelLimitAmountValue,
        syncFuelLimitControls
    } from "@/utils/fuelLimits";

    const STATUS_OPTIONS = [
        {value: `stock`, text: `На складе`},
        {value: `active`, text: `Активна`},
        {value: `blocked`, text: `Заблокирована`},
        {value: `lost`, text: `Утеряна`},
        {value: `returned`, text: `Сдана`},
        {value: `archive`, text: `Архив`}
    ];

    const STATUS_VARIANTS = {
        stock: `secondary`,
        active: `success`,
        blocked: `warning`,
        lost: `danger`,
        returned: `info`,
        archive: `dark`
    };

    const HISTORY_ACTION_LABELS = {
        created: `Создание`,
        updated: `Изменение`,
        archived: `Архивация`,
        request_assign_to_initiator: `Выдача по заявке`,
        request_assign_to_action_user: `Выдача по заявке`,
        request_issue_to_recipient: `Выдача получателю по заявке`,
        request_move_to_recipient: `Перемещение по заявке`,
        request_attach_to_request_object: `Привязка к объекту по заявке`,
        request_change_limits: `Изменение суточных лимитов по заявке`,
        request_return_card: `Сдача по заявке`,
        request_block_card: `Блокировка по заявке`,
        request_mark_lost: `Утеря по заявке`
    };

    const FIELD_LABELS = {
        card_number: `Номер карты`,
        status: `Статус`,
        provider: `Поставщик`,
        building_process_id: `Строительный процесс`,
        responsible_user_id: `Ответственный`,
        allowed_fuel_type_ids: `Виды топлива`,
        fuel_limits: `Суточные лимиты топлива`,
        expires_at: `Срок действия`,
        comment: `Комментарий`
    };

    function createEmptyForm() {
        return {
            card_number: ``,
            status: `active`,
            provider: null,
            building_process_id: null,
            responsible_user_id: null,
            allowed_fuel_type_ids: [],
            fuel_limits: [],
            expires_at: null,
            comment: null
        };
    }

    function createDefaultAccessConfig() {
        return {
            enabled: false,
            read_history_for_readers: false,
            read: {participants: []},
            write: {participants: []}
        };
    }

    function cloneJson(value) {
        return JSON.parse(JSON.stringify(value || {}));
    }

    export default {
        name: "FuelCards",
        components: {
            MultipleParticipantsConfig
        },
        computed: {
            items() {
                return this.$store.state.fuelCards.items;
            },
            accessSettings() {
                return this.$store.state.fuelCards.accessSettings || {};
            },
            canCreate() {
                return !this.archiveOnly && (
                    this.accessSettings.can_create === true
                    || (this.$can && this.$can(`create`, `fuelCards`))
                );
            },
            canManageAccess() {
                return this.accessSettings.can_manage_access === true;
            },
            computedFields() {
                return this.fields.concat([{
                    key: `actions`,
                    label: `Действия`,
                    class: `text-nowrap`
                }]);
            },
            statusOptions() {
                return STATUS_OPTIONS;
            },
            statusFilterOptions() {
                return [{value: null, text: `Все статусы`}].concat(STATUS_OPTIONS);
            },
            objectOptions() {
                const processes = this.$store.state.objectsInProcess || [];
                return processes.map(process => {
                    const name = process.object_name
                        || (process.bo && process.bo.name)
                        || `Процесс ${process.building_process_id}`;
                    const contract = process.contract_name ? ` (${process.contract_name})` : ``;
                    return {
                        value: process.building_process_id,
                        displayName: `${name}${contract}`
                    };
                }).sort((a, b) => a.displayName.localeCompare(b.displayName, `ru`));
            },
            userOptions() {
                const users = this.$store.state.users || [];
                return users
                    .filter(user => user.active || user.user_id === this.form.responsible_user_id)
                    .map(user => ({
                        value: user.user_id,
                        displayName: this.userName(user)
                    }))
                    .sort((a, b) => a.displayName.localeCompare(b.displayName, `ru`));
            },
            accessUserOptions() {
                const users = this.$store.state.users || [];
                return users
                    .filter(user => user.active && !user.isBot)
                    .map(user => ({
                        value: user.user_id,
                        text: this.userName(user)
                    }))
                    .sort((a, b) => a.text.localeCompare(b.text, `ru`));
            },
            accessDepartmentOptions() {
                const departments = this.$store.state.departments || [];
                return departments
                    .filter(department => department.is_active !== false)
                    .map(department => ({
                        value: department.id,
                        text: department.name
                    }))
                    .sort((a, b) => a.text.localeCompare(b.text, `ru`));
            },
            fuelTypeOptions() {
                return buildFuelTypeOptions(this.$store.state.fuelTypes || []);
            },
            fuelLimitTypeOptions() {
                return [{value: null, text: `Выберите топливо`}].concat(this.fuelTypeOptions);
            },
            fuelTypeById() {
                return buildFuelTypeById(this.fuelTypeOptions);
            },
            userById() {
                const result = {};
                (this.$store.state.users || []).forEach(user => {
                    result[user.user_id] = this.userName(user);
                });
                return result;
            },
            processById() {
                const result = {};
                this.objectOptions.forEach(option => {
                    result[option.value] = option.displayName;
                });
                return result;
            },
            selectedProcessOption() {
                if (!this.form.building_process_id) {
                    return null;
                }
                const found = this.objectOptions.find(option => option.value === this.form.building_process_id);
                if (found) {
                    return found;
                }
                return {
                    value: this.form.building_process_id,
                    displayName: this.currentCard && this.currentCard.object_name
                        ? this.currentCard.object_name
                        : `Процесс ${this.form.building_process_id}`
                };
            },
            selectedResponsibleOption() {
                if (!this.form.responsible_user_id) {
                    return null;
                }
                const found = this.userOptions.find(option => option.value === this.form.responsible_user_id);
                if (found) {
                    return found;
                }
                return {
                    value: this.form.responsible_user_id,
                    displayName: this.currentCard && this.currentCard.responsible_name
                        ? this.currentCard.responsible_name
                        : `Сотрудник ${this.form.responsible_user_id}`
                };
            },
            isEditMode() {
                return Boolean(this.currentCard && this.currentCard.id);
            },
            cardNumberState() {
                return this.form.card_number ? null : false;
            },
            responsibleState() {
                return this.form.responsible_user_id ? null : false;
            },
            formValidationMessages() {
                const messages = [];
                if (!this.form.card_number) {
                    messages.push(`Укажите номер топливной карты.`);
                }
                if (!this.form.responsible_user_id) {
                    messages.push(`Укажите ответственного сотрудника.`);
                }
                return messages;
            },
            isFormValid() {
                return Boolean(this.form.card_number && this.form.status && this.form.responsible_user_id);
            },
            visibleItems() {
                const query = String(this.filter || ``).toLowerCase();
                return this.items.filter(item => {
                    if (this.statusFilter && item.status !== this.statusFilter) {
                        return false;
                    }
                    if (!query) {
                        return true;
                    }
                    const values = [
                        item.card_number,
                        item.status_label,
                        item.object_name,
                        item.responsible_name,
                        item.provider,
                        item.comment,
                        (item.allowed_fuel_type_names || []).join(`, `),
                        this.formatFuelLimits(item.fuel_limits)
                    ];
                    return values.some(value => String(value || ``).toLowerCase().indexOf(query) !== -1);
                });
            },
            totalRows() {
                return this.visibleItems.length;
            }
        },
        data() {
            return {
                fields: [
                    {key: `card_number`, label: `Номер карты`, sortable: true},
                    {key: `status`, label: `Статус`, sortable: true},
                    {key: `object_name`, label: `Объект`, sortable: true},
                    {key: `responsible_name`, label: `Ответственный`, sortable: true},
                    {key: `provider`, label: `Поставщик`, sortable: true},
                    {key: `allowed_fuel_type_names`, label: `Топливо`},
                    {key: `fuel_limits`, label: `Лимиты в сутки`},
                    {key: `expires_at`, label: `Срок действия`, sortable: true}
                ],
                historyFields: [
                    {key: `created_at`, label: `Дата`, class: `text-nowrap`},
                    {key: `action`, label: `Событие`},
                    {key: `changed_by_name`, label: `Кто изменил`},
                    {key: `request`, label: `Основание`},
                    {key: `changes`, label: `Изменения`},
                    {key: `comment`, label: `Комментарий`}
                ],
                filter: null,
                statusFilter: null,
                archiveOnly: false,
                archiveScopeOptions: [
                    {value: false, text: `Действующие`},
                    {value: true, text: `Архив`}
                ],
                perPage: 200,
                pageOptions: [5, 10, 15, 20, 100, 200],
                currentPage: 1,
                showEditModal: false,
                showArchiveModal: false,
                showHistoryModal: false,
                showAccessModal: false,
                isSubmitting: false,
                isCardsLoading: true,
                isHistoryLoading: false,
                isSettingsSubmitting: false,
                errorMessage: null,
                modalErrorMessage: null,
                currentCard: null,
                form: createEmptyForm(),
                settingsForm: createDefaultAccessConfig(),
                historyRows: []
            }
        },
        mounted() {
            this.fetchAccessSettings();
            this.fetchCards();
            this.$store.dispatch(`getObjectsInProcess`).catch(() => {});
            this.$store.dispatch(`getUsers`, {full: true}).catch(() => {});
            this.$store.dispatch(`getDepartments`).catch(() => {});
            this.$store.dispatch(`getTypesOfFuel`).catch(() => {});
        },
        methods: {
            emptyForm() {
                return createEmptyForm();
            },
            async fetchCards() {
                this.isCardsLoading = true;
                try {
                    await this.$store.dispatch(`getFuelCards`, this.archiveOnly);
                } catch (err) {
                    this.errorMessage = this.errorText(err, `Ошибка загрузки топливных карт`);
                } finally {
                    this.isCardsLoading = false;
                }
            },
            fetchAccessSettings() {
                return this.$store.dispatch(`getFuelCardAccessSettings`).catch(err => {
                    this.errorMessage = this.errorText(err, `Ошибка загрузки настройки доступа`);
                });
            },
            canEditCard(card) {
                return !this.archiveOnly && card && card.can_update === true;
            },
            canArchiveCard(card) {
                return !this.archiveOnly && card && card.can_delete === true;
            },
            canReadHistory(card) {
                return card && card.can_read_history === true;
            },
            openCreateModal() {
                this.resetForm();
                this.showEditModal = true;
            },
            openEditModal(card) {
                this.currentCard = card;
                this.form = {
                    card_number: card.card_number || ``,
                    status: card.status || `active`,
                    provider: card.provider || null,
                    building_process_id: card.building_process_id || null,
                    responsible_user_id: card.responsible_user_id || null,
                    allowed_fuel_type_ids: Array.isArray(card.allowed_fuel_type_ids)
                        ? card.allowed_fuel_type_ids.slice()
                        : [],
                    fuel_limits: this.cloneFuelLimits(card.fuel_limits),
                    expires_at: card.expires_at || null,
                    comment: card.comment || null
                };
                this.showEditModal = true;
            },
            openArchiveModal(card) {
                this.currentCard = card;
                this.showArchiveModal = true;
            },
            async openHistoryModal(card) {
                this.currentCard = card;
                this.historyRows = [];
                this.showHistoryModal = true;
                this.isHistoryLoading = true;
                try {
                    this.historyRows = await this.$store.dispatch(`getFuelCardHistory`, card.id);
                } catch (err) {
                    this.errorMessage = this.errorText(err, `Ошибка загрузки истории топливной карты`);
                    this.showHistoryModal = false;
                } finally {
                    this.isHistoryLoading = false;
                }
            },
            openAccessModal() {
                const config = this.accessSettings.access_config || createDefaultAccessConfig();
                this.settingsForm = Object.assign(createDefaultAccessConfig(), cloneJson(config));
                if (!this.settingsForm.read) {
                    this.$set(this.settingsForm, `read`, {participants: []});
                }
                if (!this.settingsForm.write) {
                    this.$set(this.settingsForm, `write`, {participants: []});
                }
                this.showAccessModal = true;
            },
            async saveAccessSettings() {
                this.isSettingsSubmitting = true;
                this.errorMessage = null;
                try {
                    await this.$store.dispatch(`updateFuelCardAccessSettings`, cloneJson(this.settingsForm));
                    await this.fetchCards();
                    this.showAccessModal = false;
                } catch (err) {
                    this.errorMessage = this.errorText(err, `Ошибка сохранения настройки доступа`);
                } finally {
                    this.isSettingsSubmitting = false;
                }
            },
            async submitForm(event) {
                if (event && event.preventDefault) {
                    event.preventDefault();
                }
                if (this.isSubmitting) {
                    return;
                }

                this.isSubmitting = true;
                this.modalErrorMessage = null;
                await this.flushPendingInputChanges();
                if (!this.isFormValid) {
                    this.isSubmitting = false;
                    return;
                }
                try {
                    const payload = Object.assign({}, this.form, {
                        allowed_fuel_type_ids: this.form.allowed_fuel_type_ids.map(Number),
                        fuel_limits: this.sanitizeFuelLimits(this.form.fuel_limits)
                    });
                    if (this.isEditMode) {
                        await this.$store.dispatch(`updateFuelCard`, {
                            id: this.currentCard.id,
                            payload
                        });
                    } else {
                        await this.$store.dispatch(`createFuelCard`, payload);
                    }
                    this.showEditModal = false;
                } catch (err) {
                    this.modalErrorMessage = this.errorText(err, `Ошибка сохранения топливной карты`);
                } finally {
                    this.isSubmitting = false;
                }
            },
            async archiveCurrentCard() {
                if (!this.currentCard) {
                    return;
                }
                this.isSubmitting = true;
                this.errorMessage = null;
                try {
                    await this.$store.dispatch(`archiveFuelCard`, this.currentCard.id);
                    this.showArchiveModal = false;
                } catch (err) {
                    this.errorMessage = this.errorText(err, `Ошибка архивации топливной карты`);
                } finally {
                    this.isSubmitting = false;
                }
            },
            resetForm() {
                this.currentCard = null;
                this.form = this.emptyForm();
                this.isSubmitting = false;
                this.modalErrorMessage = null;
            },
            resetHistory() {
                this.currentCard = null;
                this.historyRows = [];
                this.isHistoryLoading = false;
            },
            resetAccessModal() {
                this.settingsForm = createDefaultAccessConfig();
                this.isSettingsSubmitting = false;
            },
            onSelectProcess(option) {
                this.form.building_process_id = option ? option.value : null;
            },
            onSelectResponsible(option) {
                this.form.responsible_user_id = option ? option.value : null;
            },
            cloneFuelLimits(value) {
                return cloneFuelLimitsValue(value);
            },
            sanitizeFuelLimits(value) {
                return sanitizeFuelLimitsValue(value);
            },
            setFuelLimitType(limit, value) {
                setFuelLimitTypeValue(limit, value, (target, key, normalizedValue) => this.$set(target, key, normalizedValue));
            },
            setFuelLimitValue(limit, value) {
                setFuelLimitAmountValue(limit, value, (target, key, normalizedValue) => this.$set(target, key, normalizedValue));
            },
            getFuelLimitByIndex(index) {
                return Array.isArray(this.form.fuel_limits)
                    ? (this.form.fuel_limits[index] || null)
                    : null;
            },
            syncFuelLimitFormControls() {
                if (typeof document === `undefined`) {
                    return;
                }

                const formSelector = `[data-fuel-card-limit-form-id="${this._uid}"]`;
                syncFuelLimitControls({
                    root: document,
                    typeSelector: `${formSelector}[data-fuel-card-limit-type]`,
                    valueSelector: `${formSelector}[data-fuel-card-limit-value]`,
                    getLimit: control => {
                        const index = Number(control.getAttribute(`data-fuel-card-limit-index`));
                        return this.getFuelLimitByIndex(index);
                    },
                    setter: (target, key, value) => this.$set(target, key, value)
                });
            },
            async flushPendingInputChanges() {
                this.syncFuelLimitFormControls();
                if (typeof document !== `undefined` && document.activeElement && document.activeElement.blur) {
                    document.activeElement.blur();
                }
                await this.$nextTick();
                await this.$nextTick();
                this.syncFuelLimitFormControls();
            },
            addFuelLimit() {
                this.form.fuel_limits.push({
                    fuel_type_id: null,
                    value: null
                });
            },
            removeFuelLimit(index) {
                this.form.fuel_limits.splice(index, 1);
            },
            formatFuelLimits(value) {
                return formatFuelLimitsValue(value, this.fuelTypeById);
            },
            statusLabel(status) {
                const found = STATUS_OPTIONS.find(option => option.value === status);
                return found ? found.text : status;
            },
            statusVariant(status) {
                return STATUS_VARIANTS[status] || `secondary`;
            },
            historyActionLabel(action) {
                return HISTORY_ACTION_LABELS[action] || action;
            },
            userName(user) {
                return [user.last_name, user.first_name].filter(Boolean).join(` `).trim();
            },
            formatDate(value) {
                if (!value) {
                    return ``;
                }
                return new Date(value).toLocaleDateString(`ru-RU`);
            },
            formatDateTime(value) {
                if (!value) {
                    return ``;
                }
                return new Date(value).toLocaleString(`ru-RU`);
            },
            formatHistoryChanges(changes) {
                if (!changes || !Object.keys(changes).length) {
                    return ``;
                }
                return Object.keys(changes)
                    .filter(key => key !== `normalized_card_number`)
                    .map(key => {
                        const change = changes[key] || {};
                        return `${FIELD_LABELS[key] || key}: ${this.formatHistoryValue(key, change.old)} -> ${this.formatHistoryValue(key, change.new)}`;
                    })
                    .join(`; `);
            },
            formatHistoryValue(key, value) {
                if (value === null || value === undefined || value === ``) {
                    return `пусто`;
                }
                if (key === `status`) {
                    return this.statusLabel(value);
                }
                if (key === `building_process_id`) {
                    return this.processById[value] || `Процесс ${value}`;
                }
                if (key === `responsible_user_id`) {
                    return this.userById[value] || `Сотрудник ${value}`;
                }
                if (key === `allowed_fuel_type_ids`) {
                    if (!Array.isArray(value) || !value.length) {
                        return `Все`;
                    }
                    return value.map(id => this.fuelTypeById[id] || id).join(`, `);
                }
                if (key === `fuel_limits`) {
                    return this.formatFuelLimits(value) || `не указаны`;
                }
                if (key === `expires_at`) {
                    return this.formatDate(value);
                }
                return String(value);
            },
            errorText(err, fallback) {
                if (err && err.response && err.response.data) {
                    if (typeof err.response.data === `string`) {
                        return err.response.data;
                    }
                    if (err.response.data.msg) {
                        return err.response.data.msg;
                    }
                }
                return fallback;
            }
        },
        watch: {
            archiveOnly() {
                this.statusFilter = null;
                this.fetchCards();
            },
            totalRows() {
                if (this.currentPage > 1 && this.currentPage > Math.ceil(this.totalRows / this.perPage)) {
                    this.currentPage = 1;
                }
            }
        }
    }
</script>

<style scoped>
.fuel-cards-page {
    min-width: 0;
}

.fuel-cards-select-invalid >>> .vs__dropdown-toggle {
    border-color: #dc3545;
}
</style>
